/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import upperFirst from 'lodash/upperFirst';
import { useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { token } from '@atlaskit/tokens';
import type { Prettify } from '@atlassian/twg-common';

import { messages } from './messages';

const styles = cssMap({
	appCard: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1f1f21',
		borderRadius: token('radius.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '32px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		height: '240px',
		minWidth: '264px',
		flex: '1 0 0',
		cursor: 'pointer',
		'@media (max-width: 30rem)': {
			width: '100%',
			minWidth: 'unset',
			height: 'auto',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			gap: '32px',
		},
	},
	appCardConnected: {
		opacity: 0.6,
		cursor: 'default',
	},
	appIcon: {
		width: '40px',
		height: '40px',
		borderRadius: token('radius.large'),
		objectFit: 'cover',
		flexShrink: 0,
		transform: 'rotate(8deg)',
	},
	appInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		width: '100%',
	},
	appName: {
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '24px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.5',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		color: token('color.text'),
		margin: 0,
	},
	appSubtitle: {
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text.subtlest'),
		margin: 0,
	},
});

export type AppCardData = {
	name: string;
	usedBy: string;
};

export type AppConnector = {
	displayName: string;
	iconUrl: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
};

type AppCardProps = Prettify<
	AppConnector & {
		usedBy: string;
	}
>;

export const AppCard = ({
	displayName,
	iconUrl,
	outboundAuthUrl,
	usedBy,
	isUserOAuthed,
}: AppCardProps): JSX.Element | null => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const [isConnected, setIsConnected] = useState(isUserOAuthed);

	const handleOAuth = useCallback(() => {
		if (isConnected) {
			return;
		}
		return auth(outboundAuthUrl)
			.then(() => {
				showFlag({
					title: formatMessage(messages.connectSuccessTitle),
					description: formatMessage(messages.connectSuccessDescription, {
						appName: displayName,
					}),
					appearance: 'success',
					isAutoDismiss: true,
				});
				setIsConnected(true);
			})
			.catch(() => {
				showFlag({
					title: formatMessage(messages.connectErrorTitle),
					description: formatMessage(messages.connectErrorDescription, {
						appName: displayName,
					}),
					appearance: 'error',
					isAutoDismiss: true,
				});
			});
	}, [outboundAuthUrl, displayName, showFlag, formatMessage, isConnected]);

	const connectLabel = isConnected
		? formatMessage(messages.connectedButtonLabel)
		: formatMessage(messages.connectButtonLabel);

	return (
		<div
			css={[styles.appCard, isConnected && styles.appCardConnected]}
			role="button"
			tabIndex={isConnected ? -1 : 0}
			aria-label={`${connectLabel} ${upperFirst(displayName)}`}
			aria-disabled={isConnected}
			onClick={handleOAuth}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleOAuth();
				}
			}}
		>
			<img src={iconUrl} alt={`${displayName} icon`} css={styles.appIcon} />
			<div css={styles.appInfo}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.appName}>{upperFirst(displayName)}</p>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.appSubtitle}>{usedBy}</p>
			</div>
		</div>
	);
};
